import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, TrendingUp, IndianRupee, Percent, Package } from 'lucide-react';

const PricingCalculator = () => {
  const [productPrice, setProductPrice] = useState(50);
  const [unitsPerDay, setUnitsPerDay] = useState(20);
  const [numberOfMachines, setNumberOfMachines] = useState(1);
  const [revenueSharePercent, setRevenueSharePercent] = useState(27.5);
  const [locationType, setLocationType] = useState('gyms');

  // Calculation logic
  const calculateMetrics = () => {
    const monthlyUnits = unitsPerDay * 30 * numberOfMachines;
    const grossRevenue = monthlyUnits * productPrice;
    const placementFee = 5000 * numberOfMachines;
    const revenueShare = (grossRevenue * revenueSharePercent) / 100;
    const totalCosts = placementFee + revenueShare;
    const netProfit = grossRevenue - totalCosts;
    const profitMargin = ((netProfit / grossRevenue) * 100).toFixed(1);
    const roi = ((netProfit / placementFee) * 100).toFixed(1);

    return {
      monthlyUnits,
      grossRevenue,
      placementFee,
      revenueShare,
      totalCosts,
      netProfit,
      profitMargin,
      roi,
    };
  };

  const metrics = calculateMetrics();

  const locationMultipliers = {
    gyms: { name: 'Gyms & Fitness', multiplier: 1.2, icon: '🏋️' },
    offices: { name: 'Corporate Offices', multiplier: 1.0, icon: '🏢' },
    hospitals: { name: 'Hospitals', multiplier: 0.9, icon: '🏥' },
    schools: { name: 'Schools', multiplier: 1.1, icon: '🏫' },
    residential: { name: 'Residential', multiplier: 0.85, icon: '🏘️' },
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card className="border-2 border-olive/20 shadow-xl">
      <CardContent className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-olive/10 rounded-xl flex items-center justify-center">
            <Calculator className="h-6 w-6 text-olive" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-forest">Partnership ROI Calculator</h3>
            <p className="text-sm text-sage">Estimate your costs and revenue potential</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div>
              <Label className="text-forest font-semibold mb-2 flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-olive" />
                Product Price (per unit)
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={productPrice}
                  onChange={(e) => setProductPrice(Number(e.target.value))}
                  className="border-sage/30 focus:border-olive"
                  min="10"
                  max="500"
                />
                <span className="text-earth font-medium">₹{productPrice}</span>
              </div>
              <Slider
                value={[productPrice]}
                onValueChange={(value) => setProductPrice(value[0])}
                min={10}
                max={500}
                step={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label className="text-forest font-semibold mb-2 flex items-center gap-2">
                <Package className="h-4 w-4 text-olive" />
                Expected Sales (units/day/machine)
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={unitsPerDay}
                  onChange={(e) => setUnitsPerDay(Number(e.target.value))}
                  className="border-sage/30 focus:border-olive"
                  min="5"
                  max="100"
                />
                <span className="text-earth font-medium">{unitsPerDay} units</span>
              </div>
              <Slider
                value={[unitsPerDay]}
                onValueChange={(value) => setUnitsPerDay(value[0])}
                min={5}
                max={100}
                step={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label className="text-forest font-semibold mb-2">Number of Machines</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={numberOfMachines}
                  onChange={(e) => setNumberOfMachines(Number(e.target.value))}
                  className="border-sage/30 focus:border-olive"
                  min="1"
                  max="20"
                />
                <span className="text-earth font-medium">{numberOfMachines} machine{numberOfMachines > 1 ? 's' : ''}</span>
              </div>
              <Slider
                value={[numberOfMachines]}
                onValueChange={(value) => setNumberOfMachines(value[0])}
                min={1}
                max={20}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label className="text-forest font-semibold mb-2 flex items-center gap-2">
                <Percent className="h-4 w-4 text-olive" />
                Revenue Share
              </Label>
              <div className="flex items-center gap-3">
                <Slider
                  value={[revenueSharePercent]}
                  onValueChange={(value) => setRevenueSharePercent(value[0])}
                  min={25}
                  max={30}
                  step={0.5}
                  className="flex-1"
                />
                <span className="text-earth font-medium w-16">{revenueSharePercent}%</span>
              </div>
            </div>

            <div>
              <Label className="text-forest font-semibold mb-2">Preferred Location Type</Label>
              <Select value={locationType} onValueChange={setLocationType}>
                <SelectTrigger className="border-sage/30 focus:border-olive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(locationMultipliers).map(([key, data]) => (
                    <SelectItem key={key} value={key}>
                      {data.icon} {data.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-sage mt-1">
                Different locations have varying sales patterns
              </p>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-gradient-to-br from-sage/5 to-moss/5 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-olive" />
              <h4 className="text-lg font-bold text-forest">Monthly Projection</h4>
            </div>

            {/* Gross Revenue */}
            <div className="bg-white rounded-lg p-4 border-l-4 border-olive">
              <p className="text-sm text-sage mb-1">Gross Revenue</p>
              <p className="text-2xl font-bold text-forest">{formatCurrency(metrics.grossRevenue)}</p>
              <p className="text-xs text-earth mt-1">{metrics.monthlyUnits.toLocaleString()} units × ₹{productPrice}</p>
            </div>

            {/* Costs Breakdown */}
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-3 flex justify-between items-center">
                <span className="text-sm text-earth">Placement Fee</span>
                <span className="font-semibold text-forest">- {formatCurrency(metrics.placementFee)}</span>
              </div>
              <div className="bg-white rounded-lg p-3 flex justify-between items-center">
                <span className="text-sm text-earth">Revenue Share ({revenueSharePercent}%)</span>
                <span className="font-semibold text-forest">- {formatCurrency(metrics.revenueShare)}</span>
              </div>
              <div className="bg-white rounded-lg p-3 flex justify-between items-center border-t-2 border-sage/20">
                <span className="text-sm font-semibold text-earth">Total Costs</span>
                <span className="font-bold text-forest">- {formatCurrency(metrics.totalCosts)}</span>
              </div>
            </div>

            {/* Net Profit */}
            <div className={`rounded-lg p-4 border-2 ${metrics.netProfit > 0 ? 'bg-olive/10 border-olive' : 'bg-red-50 border-red-300'}`}>
              <p className="text-sm font-semibold mb-1" style={{ color: metrics.netProfit > 0 ? 'hsl(82, 39%, 40%)' : '#dc2626' }}>
                Net Profit
              </p>
              <p className="text-3xl font-bold" style={{ color: metrics.netProfit > 0 ? 'hsl(142, 45%, 20%)' : '#dc2626' }}>
                {formatCurrency(metrics.netProfit)}
              </p>
              <div className="flex gap-4 mt-2">
                <div>
                  <p className="text-xs text-sage">Profit Margin</p>
                  <p className="text-sm font-semibold text-forest">{metrics.profitMargin}%</p>
                </div>
                <div>
                  <p className="text-xs text-sage">ROI</p>
                  <p className="text-sm font-semibold text-forest">{metrics.roi}%</p>
                </div>
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-sage mb-1">
                <span>Costs</span>
                <span>Profit</span>
              </div>
              <div className="h-4 bg-sage/20 rounded-full overflow-hidden flex">
                <div 
                  className="bg-orange-400 flex items-center justify-center text-xs text-white font-semibold"
                  style={{ width: `${(metrics.totalCosts / metrics.grossRevenue) * 100}%` }}
                >
                  {((metrics.totalCosts / metrics.grossRevenue) * 100).toFixed(0)}%
                </div>
                <div 
                  className="bg-olive flex items-center justify-center text-xs text-white font-semibold"
                  style={{ width: `${(metrics.netProfit / metrics.grossRevenue) * 100}%` }}
                >
                  {((metrics.netProfit / metrics.grossRevenue) * 100).toFixed(0)}%
                </div>
              </div>
            </div>

            {/* Recommendation */}
            {metrics.netProfit > 0 ? (
              <div className="bg-olive/5 border border-olive/20 rounded-lg p-3 mt-4">
                <p className="text-xs font-semibold text-olive mb-1">💡 Recommendation</p>
                <p className="text-xs text-earth">
                  {metrics.roi > 100 
                    ? 'Excellent ROI! Your product is well-suited for our vending network.'
                    : metrics.roi > 50
                    ? 'Good potential! Consider optimizing product pricing or sales volume.'
                    : 'Moderate returns. Discuss with our team about better locations or product mix.'}
                </p>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                <p className="text-xs font-semibold text-red-600 mb-1">⚠️ Notice</p>
                <p className="text-xs text-red-700">
                  Current projection shows negative returns. Try adjusting pricing or sales volume, or contact us for optimization strategies.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 pt-6 border-t border-sage/20">
          <p className="text-xs text-sage text-center">
            <strong>Note:</strong> These are estimates based on your inputs. Actual results may vary based on location performance, 
            product demand, and seasonal factors. Our team will provide detailed projections during onboarding.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingCalculator;
